<template>
  <MediumModal
    v-if="transfer"
    :is-open="open"
    @close="close"
  >
    <template #header> Drivers for Transfer #{{ transfer.reference_number }} </template>

    <!-- HEADER -->
    <div class="bg-gray-200 -mt-3 -mx-6 py-4 px-7 flex items-center justify-between">
      <div class="flex gap-3 items-center">
        <Menu
          as="div"
          class="relative text-right -mt-1 inline-block"
        >
          <div>
            <MenuButton
              class="inline-flex items-center justify-center w-full rounded-full border border-gray-400 px-3 py-1 bg-gray-100 text-sm font-medium text-blue-600 hover:bg-gray-300"
            >
              {{ selectedOrigin ? selectedOrigin.name : defaultLocationName }}
            </MenuButton>
          </div>
        </Menu>
        <span class="font-medium text-xs">TO</span>
        <Menu
          as="div"
          class="relative text-right -mt-1 inline-block"
        >
          <div>
            <MenuButton
              class="inline-flex items-center justify-center w-full rounded-full border border-gray-400 px-3 py-1 bg-gray-100 text-sm font-medium text-blue-600 hover:bg-gray-300"
            >
              {{ selectedDestination ? selectedDestination.name : 'NA' }}
            </MenuButton>
          </div>
        </Menu>
      </div>
    </div>
    <!-- END HEADER -->

    <!-- BODY -->
    <div>
      <!-- Current drivers list -->
      <div class="p-1 divide-y divide-gray-400">
        <div class="grid grid-cols-6 gap-10 py-3">
          <div class="col-span-1 text-left text-xs uppercase text-gray-600 font-semibold">
            <span>ID</span>
          </div>
          <div class="col-span-4 text-left text-xs uppercase text-gray-600 font-semibold">
            <span>Driver</span>
          </div>
          <div class="col-span-1 text-left text-xs uppercase text-gray-600 font-semibold"></div>
        </div>
        <div
          v-for="item in selectedDrivers"
          class="grid grid-cols-6 gap-10 py-3 items-center"
        >
          <div class="col-span-1 text-left flex flex-col leading-tight">
            <span class="uppercase font-semibold text-sm">
              {{ item.id }}
            </span>
          </div>
          <div class="col-span-4 text-left text-sm uppercase text-gray-600 font-semibold">
            <span class="text-gray-600 text-sm font-semibold">
              {{ item.display }}
            </span>
          </div>
          <div class="col-span-1 text-center text-sm uppercase text-gray-600 font-semibold">
            <a @click="removeDriver(item.id)">
              <img
                :src="asset('images/minus.svg')"
                style="width: 18px"
                class="text-gray-300 mx-auto"
              />
            </a>
          </div>
        </div>
      </div>
      <!-- END Current drivers list -->

      <!-- Select new driver -->
      <div class="border-t-2 border-blue-600 mt-3">
        <div class="mt-2 py-2">
          <div class="grid grid-cols-6 gap-5 items-center divide-none border-0 border-none">
            <div class="col-span-6 flex items-center gap-2">
              <Menu
                as="div"
                class="relative text-left inline-block w-full"
              >
                <div>
                  <MenuButton
                    class="inline-flex items-center w-full justify-between rounded border border-gray-400 px-3 py-2 bg-gray-100 text-sm font-medium text-blue-600 hover:bg-gray-300"
                  >
                    Select Driver to Add
                    <ChevronDownIcon
                      class="-mr-1 ml-2 h-4 w-4 text-gray-400"
                      aria-hidden="true"
                    />
                  </MenuButton>
                </div>
                <transition
                  enter-active-class="transition ease-out duration-100"
                  enter-from-class="opacity-0 scale-95"
                  enter-to-class="opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="opacity-100 scale-100"
                  leave-to-class="opacity-0 scale-95"
                >
                  <MenuItems
                    class="justify-end origin-top-right absolute right-0 mt-2 w-full rounded-md shadow-lg bg-gray-100 divide-y divide-gray-400 focus:outline-none z-50"
                  >
                    <div class="">
                      <MenuItem
                        v-for="driver in drivers"
                        v-slot="{ active }"
                      >
                        <a
                          @click="appendDriver(driver.id)"
                          href="#"
                          :class="[
                            active ? 'bg-gray-400 text-gray-900' : 'text-gray-700',
                            'group flex items-center px-4 py-2 text-sm hover:bg-blue-600 hover:text-gray-100'
                          ]"
                        >
                          {{ `#${driver.id}: ${driver.display}` }}
                        </a>
                      </MenuItem>
                    </div>
                  </MenuItems>
                </transition>
              </Menu>
            </div>
            <!--                        <div class="col-span-1 text-center text-sm uppercase text-gray-600 font-semibold">-->
            <!--                            <a v-if="selectedDriverId" @click="appendDriver(selectedDriverId)">-->
            <!--                                <img src="/images/plus.svg" style="width: 18px;" class="text-gray-300 mx-auto" />-->
            <!--                            </a>-->
            <!--                        </div>-->
          </div>
        </div>
      </div>
      <!-- END Select new driver -->
    </div>
    <!-- END BODY -->

    <template #footer>
      <div class="flex items-center justify-end">
        <div class="flex justify-end">
          <button
            @click.prevent="submit()"
            :disabled="saving"
            type="button"
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
          >
            {{ submitText }}
          </button>
        </div>
      </div>
    </template>
  </MediumModal>
</template>

<script>
import MediumModal from '@/Components/Modals/MediumModal.vue'
import { XCircleIcon, SearchIcon, ChevronDownIcon } from '@heroicons/vue/solid'
import { PlusCircleIcon, MinusCircleIcon, CalendarIcon, ExclamationCircleIcon } from '@heroicons/vue/outline'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import getsErrors from '@/Mixins/getsErrors'
import interactsWithCurrentLocation from '@/Mixins/interactsWithCurrentLocation'
import accessesCompany from '@/Mixins/accessesCompany'

export default {
  mixins: [getsErrors, interactsWithCurrentLocation, accessesCompany],

  components: {
    MediumModal,
    XCircleIcon,
    SearchIcon,
    PlusCircleIcon,
    MinusCircleIcon,
    ChevronDownIcon,
    CalendarIcon,
    ExclamationCircleIcon,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems
  },

  props: {
    open: {
      type: Boolean,
      default: false
    },
    actionText: {
      type: String,
      default: 'Save'
    },
    transfer: {
      type: Object,
      required: true
    },
    locations: {
      type: Array,
      default() {
        return []
      }
    },
    drivers: {
      type: Array,
      default() {
        return []
      }
    }
  },

  emits: ['update:open', 'saved'],

  created() {
    console.log('created()', this.transfer)
    this.initDriverIds()
  },

  data() {
    return {
      driverIds: [],
      selectedDriverId: null,
      form: this.$inertia.form({
        drivers: []
      }),
      saving: false
    }
  },

  watch: {
    transfer(newVal, oldVal) {
      console.log('transfer changed', newVal, oldVal)

      if (newVal) {
        this.initDriverIds()
      }
    }
  },

  computed: {
    submitText() {
      if (this.saving) {
        return 'Saving...'
      }

      return this.actionText
    },
    selectedOrigin() {
      if (!this.transfer?.origin_id) {
        return null
      }

      return this.locations.find((location) => location.id === this.transfer.origin_id)
    },
    selectedDestination() {
      if (!this.transfer?.destination_id) {
        return null
      }

      return this.locations.find((location) => location.id === this.transfer.destination_id)
    },
    existingDriverIds() {
      return _.map(this.transfer?.drivers || [], (user) => {
        return user.id
      })
    },
    selectedDriver() {
      if (!this.selectedDriverId) {
        return null
      }

      return _.find(this.drivers, (user) => {
        return user.id === this.selectedDriverId
      })
    },
    selectedDrivers() {
      return this.getDriversForIds(this.driverIds)
    }
  },

  methods: {
    displayUser(user) {
      return user.name || user.email
    },
    getDriversForIds(ids) {
      return _.filter(this.drivers, (user) => {
        return _.indexOf(ids, user.id) > -1
      })
    },
    initDriverIds() {
      this.driverIds = this.existingDriverIds
    },
    selectDriver(id) {
      this.selectedDriverId = id
    },
    appendDriver(id) {
      this.driverIds = _.uniq(_.concat(this.driverIds, id))
    },
    removeDriver(id) {
      this.driverIds = _.filter(this.driverIds, (driverId) => {
        return driverId !== id
      })
    },
    close() {
      this.$emit('update:open', false)
    },
    emitSaved() {
      this.$emit('saved')
    },
    submit() {
      this.saving = true

      this.form.drivers = this.driverIds

      this.form.put(route('transfers.drivers.store', { transfer: this.transfer.id }), {
        // errorBag: 'receiveTransfer',
        onSuccess: (response) => {
          this.emitSaved()
        },
        onFinish: (response) => {
          this.form.reset()
          this.close()
          this.saving = false
        }
      })
    }
  }
}
</script>
